import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { v4 as uuidv4 } from 'uuid';

import Field from 'core/components/form/form.v2';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import { AppliedTag } from 'common/components/farm/applied-tag';
import useStyles from '../liability-coverages-styles';
import AddFormContainer from 'common/components/farm/add-form-container';

const validationSchema = Yup.object().shape({
  description: Yup.string().required('Description is required'),
  amount: Yup.number().required('Estimated receipts is required').positive('Must be a positive number')
});

const FIELD_NAME = 'farmLiabilityCoverage.customFarmWorkItems';

const CustomFarming = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const items = getIn(values, FIELD_NAME) || [];

  return (
    <div className={classes.sectionContainer} id="farmLiabilityCoverage.customFarmWorkItems">
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Custom farming</div>}
        contentRight={items.length > 0 ? <AppliedTag /> : null}
        initialOpen={items.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={items.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {items.map((_item, index) => (
            <CollapsibleListItem
              key={`custom-farming-${index}`}
              title={`Custom farming ${String(index + 1).padStart(2, '0')}`}
              rightSideValues={[]}
              onDelete={() => {
                const newItems = items.filter((_, i) => i !== index);
                setFieldValue(FIELD_NAME, newItems);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
              }}
              disabled={disabled}
            >
              <Field
                xs={12}
                type="textarea"
                id={`${FIELD_NAME}[${index}].description`}
                name={`${FIELD_NAME}[${index}].description`}
                mode="dark"
                label="Description"
                disabled={disabled}
                onChange={onChangeActive}
              />
              <Field
                xs={6}
                type="numeric"
                id={`${FIELD_NAME}[${index}].amount`}
                name={`${FIELD_NAME}[${index}].amount`}
                mode="dark"
                label="Estimated receipts (per year)"
                disabled={disabled}
                format={{ prefix: '$', thousandSeparator: true, decimalScale: 2 }}
                onChange={onChangeActive}
              />
              <Field
                xs={6}
                type="value"
                id={`${FIELD_NAME}[${index}].includesSpecificActivities`}
                name={`${FIELD_NAME}[${index}].includesSpecificActivities`}
                mode="dark"
                label="Application of pesticides or herbicides"
                formatter={() => 'Excluded'}
              />
            </CollapsibleListItem>
          ))}

          <AddFormContainer className={classes.addFormContainer}>
            <Formik
              initialValues={{
                description: '',
                amount: '',
                includesSpecificActivities: false
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema}
              onSubmit={(val, { resetForm }) => {
                const newItem = {
                  id: uuidv4(),
                  type: 'customFarming',
                  description: val.description,
                  amount: Number(val.amount),
                  includesSpecificActivities: false
                };
                setFieldValue(FIELD_NAME, [...items, newItem]);
                setFieldTouched(FIELD_NAME);
                onChangeActive();
                resetForm();
              }}
            >
              {({ submitForm }) => (
                <Grid container spacing={2}>
                  <Field
                    xs={12}
                    type="textarea"
                    id="description"
                    name="description"
                    mode="dark"
                    label="Description (required)"
                    disabled={disabled}
                  />
                  <Field
                    xs={6}
                    type="numeric"
                    id="amount"
                    name="amount"
                    mode="dark"
                    label="Estimated receipts (per year)"
                    disabled={disabled}
                    format={{ prefix: '$', thousandSeparator: true, decimalScale: 2 }}
                  />
                  <Field
                    xs={6}
                    type="value"
                    id="includesSpecificActivities"
                    name="includesSpecificActivities"
                    mode="dark"
                    label="Application of pesticides or herbicides"
                    formatter={() => 'Excluded'}
                  />
                  <Grid container alignItems="center" className={classes.addButtonRow}>
                    <Button
                      type="button"
                      mode="big"
                      variant="contained"
                      color="secondary"
                      className={classes.addButton}
                      disabled={disabled}
                      onClick={submitForm}
                    >
                      Add Item
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Formik>
          </AddFormContainer>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

CustomFarming.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default CustomFarming;
